import { z } from "zod";
import type { AiEstimate, MaterialGrade, PriceSnapshot } from "../estimate.js";
import { MATERIAL_GRADES, PLANES, polygonSchema } from "../estimate.js";
import type { Category } from "../api.js";
import {
  aiToGroupInputs,
  computeEstimate,
  type EstimateGroupInput,
  type EstimateInput,
  type EstimateResult,
} from "../pricing/computeEstimate.js";
import { JOB_STATUSES, type JobStatus } from "./stateMachine.js";

const money = z.number().nonnegative().max(100_000_000);

/** ค่าดำเนินการ/กำไรตั้งต้น (%) ตามที่ตกลง; ภายหลังย้ายเป็น config ที่ Admin ปรับได้ (Phase 4) */
export const DEFAULT_OVERHEAD_PCT = 20;

/** จำนวนงานย่อย (Tab) สูงสุดที่แสดงพร้อมกันของหนึ่งงาน */
export const MAX_TABS = 8;
/** ลำดับพื้นที่ทั้งหมดที่เป็นไปได้ (พื้นที่ของ AI สูงสุด 8 + ที่ผู้ใช้เพิ่มสูงสุด 8) */
const MAX_AREA_INDEX = 15;

export type WorkArea = AiEstimate["work_areas"][number];

/**
 * งานย่อยที่ผู้ใช้เพิ่มเอง (Tab ใหม่): เลือกประเภทงานและวาดขอบเขตเอง ขนาดกรอกเองในการแก้ค่าของพื้นที่นั้น
 * ลำดับ areaIndex ของพื้นที่นี้คือ ai.work_areas.length + ตำแหน่งในอาร์เรย์ addedAreas
 */
export const addedAreaSchema = z.object({
  category_id: z.string().min(1).max(60),
  label_th: z.string().trim().min(1).max(60),
  plane: z.enum(PLANES),
  polygon: polygonSchema.optional(),
});
export type AddedArea = z.infer<typeof addedAreaSchema>;

/** การแก้ไขของผู้ใช้ต่อหนึ่งพื้นที่ทำงาน (areaIndex = ลำดับใน ai.work_areas ตามด้วยพื้นที่ที่เพิ่มเอง) */
export const groupEditSchema = z.object({
  areaIndex: z.number().int().min(0).max(MAX_AREA_INDEX),
  /** ชื่องานย่อย (ชื่อ Tab) ที่ผู้ใช้ตั้งเอง */
  title: z.string().trim().min(1).max(60).optional(),
  polygon: polygonSchema.optional(),
  dims: z
    .object({
      width_m: z.number().nonnegative().max(500).nullable().optional(),
      length_m: z.number().nonnegative().max(500).nullable().optional(),
      height_m: z.number().nonnegative().max(100).nullable().optional(),
      count: z.number().int().nonnegative().max(10000).nullable().optional(),
      area_m2: z.number().nonnegative().max(100000).nullable().optional(),
      doorDeduct_m2: z.number().nonnegative().max(1000).optional(),
    })
    .optional(),
  wastePct: z.number().min(0).max(1).optional(),
  tileSizeM: z.number().positive().max(5).optional(),
  overrides: z
    .object({
      laborRate: money.optional(),
      materialRate: money.optional(),
      qty: z.record(z.string().max(60), z.number().nonnegative().max(1_000_000)).optional(),
      unitPrice: z.record(z.string().max(60), money).optional(),
      removed: z.array(z.string().max(60)).max(60).optional(),
    })
    .optional(),
  custom: z
    .array(
      z.object({
        key: z.string().min(1).max(60),
        name_th: z.string().min(1).max(120),
        qty: z.number().nonnegative().max(1_000_000),
        unit: z.string().max(20),
        unitPrice: money,
      }),
    )
    .max(40)
    .optional(),
});
export type GroupEdit = z.infer<typeof groupEditSchema>;

export const jobEditsSchema = z.object({
  grade: z.enum(MATERIAL_GRADES).optional(),
  discount: z
    .object({ type: z.enum(["amount", "percent"]), value: z.number().nonnegative().max(100_000_000) })
    .optional(),
  overheadPct: z.number().min(0).max(100).optional(),
  vat: z.boolean().optional(),
  groups: z.array(groupEditSchema).max(16).default([]),
  /** งานย่อยที่ผู้ใช้เพิ่ม และพื้นที่ (ตาม areaIndex) ที่ผู้ใช้ลบออกจาก Tab */
  addedAreas: z.array(addedAreaSchema).max(MAX_TABS).optional(),
  removedAreas: z.array(z.number().int().min(0).max(MAX_AREA_INDEX)).max(16).optional(),
});
export type JobEdits = z.infer<typeof jobEditsSchema>;

/** พื้นที่ทั้งหมด: ของ AI ตามด้วยที่ผู้ใช้เพิ่ม (ยังไม่หักที่ลบ) ลำดับตรงกับ areaIndex */
export function fullAreas(ai: AiEstimate, edits?: Pick<JobEdits, "addedAreas">): WorkArea[] {
  const added: WorkArea[] = (edits?.addedAreas ?? []).map((a) => ({
    category_id: a.category_id,
    label_th: a.label_th,
    polygon: a.polygon ?? [],
    plane: a.plane,
    estimated_dimensions: { width_m: null, length_m: null, height_m: null, count: null },
    dimension_confidence: 0,
  }));
  return [...ai.work_areas, ...added];
}

/** AI เดิมที่เติมพื้นที่ที่ผู้ใช้เพิ่มเข้าไป (ใช้แปลงเป็น group input ได้ตามปกติ) */
export const withAddedAreas = (ai: AiEstimate, edits?: Pick<JobEdits, "addedAreas">): AiEstimate => ({
  ...ai,
  work_areas: fullAreas(ai, edits),
});

/** พื้นที่ที่ยังแสดงเป็น Tab พร้อมลำดับ areaIndex และชื่อที่ผู้ใช้ตั้ง (ใช้กับรายงาน/หน้าแชร์ที่ต้องรู้ชื่อและขนาดของแต่ละงานย่อย) */
export function effectiveAreas(ai: AiEstimate, edits?: JobEdits): { index: number; area: WorkArea }[] {
  const removed = new Set(edits?.removedAreas ?? []);
  const titles = new Map(
    (edits?.groups ?? []).filter((g) => g.title).map((g) => [g.areaIndex, g.title as string]),
  );
  return fullAreas(ai, edits)
    .map((area, index) => ({
      index,
      area: titles.has(index) ? { ...area, label_th: titles.get(index) as string } : area,
    }))
    .filter((x) => !removed.has(x.index));
}

/** ตรวจการเพิ่ม/ลบงานย่อย: คืนรหัสข้อผิดพลาด หรือ null เมื่อใช้ได้ (server ใช้ก่อนบันทึก ส่วนหน้าเว็บใช้ปิดปุ่ม) */
export function validateAreaEdits(
  ai: AiEstimate,
  edits: JobEdits | undefined,
): "invalid_area" | "no_active_area" | "too_many_areas" | null {
  const total = fullAreas(ai, edits).length;
  const removed = edits?.removedAreas ?? [];
  if (removed.some((i) => i >= total) || (edits?.groups ?? []).some((g) => g.areaIndex >= total))
    return "invalid_area";
  const active = effectiveAreas(ai, edits).length;
  if (active < 1) return "no_active_area";
  if (active > MAX_TABS) return "too_many_areas";
  return null;
}

/** ภาพถ่ายราคาจากข้อมูลหมวดปัจจุบัน (ใช้กับหมวดที่ผู้ใช้เพิ่มทีหลัง) source ถ้าไม่ส่งใช้ข้อมูลเท่าที่หมวดมี */
export function snapshotFromCategory(c: Category, source?: PriceSnapshot["source"]): PriceSnapshot {
  return {
    category_id: c.category_id,
    unit: c.unit,
    labor_min_thb: c.labor_min_thb,
    labor_max_thb: c.labor_max_thb,
    material_min_thb: c.material_min_thb,
    material_max_thb: c.material_max_thb,
    price_version: c.price_version,
    source: source ?? {
      source_id: c.price_source_id,
      name: "",
      kind_label_th: "",
      url: "",
      effective_from: "",
    },
  };
}

/** หมวดของงานย่อยที่เพิ่มเอง ที่ยังไม่มีภาพถ่ายราคาในงาน */
export const missingSnapshotCategories = (
  edits: JobEdits | undefined,
  snapshots: readonly PriceSnapshot[],
): string[] => [
  ...new Set(
    (edits?.addedAreas ?? [])
      .map((a) => a.category_id)
      .filter((id) => !snapshots.some((s) => s.category_id === id)),
  ),
];

/**
 * สร้าง EstimateInput จากผล AI + ราคา ณ วันประเมิน + การแก้ไขของผู้ใช้ ใช้ฟังก์ชันเดียวกันทั้ง client และ server
 * edits = undefined คือค่า AI ล้วน (ai_value)
 */
export function buildEstimateInput(
  ai: AiEstimate,
  snapshots: readonly PriceSnapshot[],
  edits?: JobEdits,
  defaults?: { overheadPct?: number },
): EstimateInput {
  const grade: MaterialGrade = edits?.grade ?? ai.recommended_material_grade;
  const { groups } = aiToGroupInputs(withAddedAreas(ai, edits), snapshots, grade);
  const byArea = new Map((edits?.groups ?? []).map((g) => [g.areaIndex, g]));
  const removed = new Set(edits?.removedAreas ?? []);
  const indexOf = (g: EstimateGroupInput, i: number): number => {
    // id ของกลุ่มจาก aiToGroupInputs คือ area-<index> ตรงกับ areaIndex ของพื้นที่ทั้งหมด (AI ตามด้วยที่ผู้ใช้เพิ่ม)
    const idx = Number(String(g.id ?? "").replace("area-", ""));
    return Number.isInteger(idx) ? idx : i;
  };
  const merged: EstimateGroupInput[] = groups
    .filter((g, i) => !removed.has(indexOf(g, i)))
    .map((g) => {
      const e = byArea.get(indexOf(g, 0));
      if (!e) return g;
      const dimsEdited = e.dims !== undefined;
      return {
        ...g,
        ...(e.title ? { label_th: e.title } : {}),
        dims: { ...g.dims, ...(e.dims ?? {}) },
        dimsSource: dimsEdited ? "user" : g.dimsSource,
        wastePct: e.wastePct ?? g.wastePct,
        tileSizeM: e.tileSizeM ?? g.tileSizeM,
        overrides: e.overrides ?? g.overrides,
        custom: e.custom ?? g.custom,
      };
    });
  return {
    groups: merged,
    discount: edits?.discount,
    overheadPct: edits?.overheadPct ?? defaults?.overheadPct ?? DEFAULT_OVERHEAD_PCT,
    vat: edits?.vat,
  };
}

export function computeJobResults(
  ai: AiEstimate,
  snapshots: readonly PriceSnapshot[],
  edits?: JobEdits,
  defaults?: { overheadPct?: number },
): { ai_value: EstimateResult; user_value: EstimateResult } {
  return {
    ai_value: computeEstimate(buildEstimateInput(ai, snapshots, undefined, defaults)),
    user_value: computeEstimate(buildEstimateInput(ai, snapshots, edits, defaults)),
  };
}

/** ----- สัญญา API งาน (Phase 3) ----- */
export const customerSchema = z.object({
  name: z.string().trim().min(1).max(120),
  phone: z.string().trim().max(30).optional(),
  address: z.string().trim().max(300).optional(),
});

/** POST /jobs  402 {error:"quota", reason:"save_limit"} เมื่อ Free บันทึกครบ */
export const saveJobRequestSchema = z.object({
  estimateId: z.string().uuid(),
  title: z.string().trim().min(1).max(160),
  customer: customerSchema,
  edits: jobEditsSchema.optional(),
});
export type SaveJobRequest = z.infer<typeof saveJobRequestSchema>;

/** PUT /jobs/{id}/estimate  บันทึกค่าที่แก้ใหม่เป็นเวอร์ชันถัดไป (ไม่ใช้ AI ไม่หักโควตา) */
export const updateEditsRequestSchema = z.object({ edits: jobEditsSchema });

export const patchJobRequestSchema = z.object({
  title: z.string().trim().min(1).max(160).optional(),
  customer: customerSchema.optional(),
  plannedStart: z.string().date().nullable().optional(),
  plannedFinish: z.string().date().nullable().optional(),
  actualStart: z.string().date().nullable().optional(),
  actualFinish: z.string().date().nullable().optional(),
  progressOverride: z.number().int().min(0).max(99).nullable().optional(),
});

export const changeStatusRequestSchema = z.object({
  to: z.enum(JOB_STATUSES),
  note: z.string().trim().max(500).optional(),
  photoKey: z.string().max(200).optional(),
});

export const milestoneRequestSchema = z.object({
  label: z.string().trim().min(1).max(80),
  amount_thb: z.number().positive().max(100_000_000),
  dueDate: z.string().date().optional(),
});
export const patchMilestoneRequestSchema = z.object({
  label: z.string().trim().min(1).max(80).optional(),
  amount_thb: z.number().positive().max(100_000_000).optional(),
  dueDate: z.string().date().nullable().optional(),
  paidAt: z.string().date().nullable().optional(),
});

export interface JobStatusEventDto {
  eventId: string;
  from: JobStatus | null;
  to: JobStatus;
  note?: string;
  photoUrl?: string;
  by: string;
  at: string;
}
export interface MilestoneDto {
  milestoneId: string;
  label: string;
  amount_thb: number;
  dueDate?: string;
  paidAt?: string;
}
export interface JobSummaryDto {
  jobId: string;
  title: string;
  customerName: string;
  customerAddress?: string;
  /** รูปแรกของงาน (presigned อายุสั้น) */
  thumbUrl?: string;
  status: JobStatus;
  progress: number;
  grandTotal: number;
  categoryIds: string[];
  createdAt: string;
  updatedAt: string;
}
export interface JobDetailDto extends JobSummaryDto {
  customer: z.infer<typeof customerSchema>;
  estimateId: string;
  estimateVersion: number;
  /** ผู้ผลิตผลประเมิน (model.id = "manual" คือกรอกขนาดเอง ใช้ isManualModel) */
  model?: { id: string; promptVersion: string };
  beforeHold: string | null;
  progressOverride: number | null;
  dates: { plannedStart?: string; plannedFinish?: string; actualStart?: string; actualFinish?: string };
  ai: AiEstimate;
  priceSnapshots: PriceSnapshot[];
  photos: { key: string; url: string }[];
  edits: JobEdits;
  results: { ai_value: EstimateResult; user_value: EstimateResult };
  events: JobStatusEventDto[];
  milestones: MilestoneDto[];
  /** อ่านได้อย่างเดียว เมื่อแพ็กเกจเสียเงินหมดอายุ ฯลฯ (ยังเปลี่ยนสถานะได้) */
  quotationId?: string;
}
